import { ReactNode } from "react";
import Image from "next/image";
import Overline from "~/componentsDS/Typography/Overline";
import Heading from "~/componentsDS/Typography/Heading";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import { cn } from "~/lib/utils";

type GettingStartedCarouselStepCardVariant = "default" | "compact";

const VARIANT_CLASSES: Record<
    GettingStartedCarouselStepCardVariant,
    { info: string; eyebrow: string; title: string }
> = {
    default: {
        info: "gap-[13px] px-[14px] py-[22px]",
        eyebrow: "text-[14.4px] tracking-[0.96px] md:tracking-[1.21px] mb-3 md:mb-[14px]",
        title: "text-[18px] md:text-[20px] mb-1",
    },
    compact: {
        info: "gap-[11px] px-[11px] py-5",
        eyebrow: "text-[14px] tracking-[0.96px] md:tracking-[1.21px] mb-[11px]",
        title: "text-[18px] mb-[2px]",
    },
};

type GettingStartedCarouselStepCardProps = {
    imageUrl: string;
    icon: ReactNode;
    title: string;
    description: ReactNode;
    stepNumber: number;
    eyebrow?: ReactNode;
    imageAlt?: string;
    variant?: GettingStartedCarouselStepCardVariant;
};

const GettingStartedCarouselStepCard = ({
    imageUrl,
    icon,
    title,
    description,
    stepNumber,
    eyebrow,
    imageAlt,
    variant = "default",
}: GettingStartedCarouselStepCardProps) => {
    const variantClasses = VARIANT_CLASSES[variant];

    return (
        <div className="flex flex-col w-full h-full rounded-2xl overflow-hidden">
            <div className="relative aspect-[416/315]">
                <Image
                    src={imageUrl}
                    alt={imageAlt ?? `Step ${stepNumber} illustration`}
                    fill
                    sizes="
                        (max-width: 767px) 100vw,
                        (max-width: 1179px) 50vw,
                        33vw
                    "
                    quality={95}
                    loading="lazy"
                    className="object-cover object-center"
                />
            </div>
            <div className={cn("flex flex-1 bg-gray100", variantClasses.info)}>
                <div className="shrink-0">
                    {icon}
                </div>
                <div className="flex flex-col flex-1">
                    <Overline weight="500" className={cn("text-primary300", variantClasses.eyebrow)}>
                        {eyebrow ?? `Step ${stepNumber}`}
                    </Overline>
                    <Heading as="h3" className={cn("font-medium text-primary500", variantClasses.title, "leading-[1.2]")}>{title}</Heading>
                    <Paragraph className="text-primary500">{description}</Paragraph>
                </div>
            </div>
        </div>
    );
};

export default GettingStartedCarouselStepCard;
